import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, TextField, MenuItem, Button, CircularProgress } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import useThemeStyles from '../../../../../hooks/useThemeStyles';

export const NOTE_CATEGORIES = [
  'General',
  'Character',
  'Plot',
  'Setting',
  'Research',
  'Worldbuilding',
  'Dialogue',
  'Theme',
  'Conflict',
  'Other',
];

/**
 * Dumb inline form for creating a note. Calls onSubmit({ title, content, category }).
 */
const NoteQuickAddForm = ({ onSubmit, disabled }) => {
  const { colors } = useThemeStyles();
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('General');
  const [saving, setSaving] = useState(false);

  const textFieldSx = {
    '& .MuiInputLabel-root': { color: colors.text.secondary },
    '& .MuiInputLabel-root.Mui-focused': { color: colors.interactive.primary },
    input: { color: colors.text.primary },
    textarea: { color: colors.text.primary },
    '& .MuiSelect-select': { color: colors.text.primary },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: colors.border.subtle },
      '&:hover fieldset': { borderColor: colors.interactive.primary },
      '&.Mui-focused fieldset': { borderColor: colors.interactive.primary },
    },
  };

  const reset = () => {
    setTitle('');
    setContent('');
    setCategory('General');
    setExpanded(false);
  };

  const handleSubmit = async () => {
    if (!title.trim() || saving) return;
    setSaving(true);
    const ok = await onSubmit({ title: title.trim(), content, category });
    setSaving(false);
    if (ok) {
      reset();
    }
  };

  if (!expanded) {
    return (
      <Button
        variant="outlined"
        size="small"
        fullWidth
        startIcon={<AddIcon />}
        onClick={() => setExpanded(true)}
        disabled={disabled}
        sx={{
          textTransform: 'none',
          color: colors.text.primary,
          borderColor: colors.border.subtle,
          '&:hover': {
            backgroundColor: colors.sidebar.headerHover,
            borderColor: colors.interactive.primary,
          },
        }}
      >
        Add Note
      </Button>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <TextField
        label="Title"
        size="small"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        autoFocus
        fullWidth
        sx={textFieldSx}
      />
      <TextField
        label="Content"
        size="small"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        multiline
        minRows={2}
        maxRows={6}
        fullWidth
        sx={textFieldSx}
      />
      <TextField
        select
        label="Category"
        size="small"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        fullWidth
        sx={textFieldSx}
      >
        {NOTE_CATEGORIES.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button
          size="small"
          onClick={reset}
          disabled={saving}
          sx={{ textTransform: 'none', color: colors.text.secondary }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSubmit}
          disabled={!title.trim() || saving}
          sx={{ textTransform: 'none' }}
        >
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

NoteQuickAddForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

NoteQuickAddForm.defaultProps = {
  disabled: false,
};

export default NoteQuickAddForm;
